import { Message, MessageStatus } from '@sr/types';

type StatusMap<T> = Record<MessageStatus, T>;

type Score = {
  percent: number;
  grade: string;
  comment: string;
};

export default function calculateScore(messages: Message[]): Score {
  const statusCounts = getStatusCounts(messages);
  const percent = calculatePercent(statusCounts);
  const grade = calculateGrade(percent);
  const comment = getComment(percent);

  return {
    percent,
    grade,
    comment,
  };
}

function getStatusCounts(messages: Message[]): StatusMap<number> {
  const counts: StatusMap<number> = {
    error: 0,
    warning: 0,
    info: 0,
    ok: 0,
  };

  messages.forEach((message) => {
    counts[message.status] = counts[message.status] + 1;
  });

  return counts;
}

function calculatePercent(statusCounts: StatusMap<number>): number {
  // How much to deduct for an error of each status
  const deductions: StatusMap<number> = {
    error: 10,
    warning: 5,
    info: 2,
    ok: 0,
  } as const;

  let percent = 100;

  const statuses = Object.keys(statusCounts) as MessageStatus[];
  statuses.forEach((status) => {
    percent = percent - deductions[status] * statusCounts[status];
  });

  return Math.max(percent, 0);
}

function calculateGrade(percent: number): string {
  switch (true) {
    case percent === 100:
      return 'A+';
    case percent > 95:
      return 'A';
    case percent > 90:
      return 'A-';
    case percent > 85:
      return 'B+';
    case percent > 80:
      return 'B';
    case percent > 75:
      return 'B-';
    case percent > 70:
      return 'C+';
    case percent > 65:
      return 'C';
    case percent > 60:
      return 'C-';
    default:
      return 'D';
  }
}

function getComment(percent: number): string {
  switch (true) {
    case percent === 100:
      return 'Your resume is perfectly suited to start lining up interviews!';
    case percent > 89:
      return "Looking good! Only a couple of improvements, and you'll be busy scheduling interviews.";
    case percent > 70:
      return "You're off to a good start, but you're not quite ready. You can only apply once, so make it count!";
    case percent > 60:
      return "You're not quite ready to apply. Address the suggestions to increase your odds of getting an interview.";
    default:
      return "Don't risk applying before improving your resume. Follow the suggestions, and you'll be ready in no time.";
  }
}
